import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

export default function Contact() {
  useSeo({
    title: "Contact | Veterans Disability Education",
    description: "Questions about your VA claim strategy? Reach Veterans Disability Education or book a free 20-minute consult.",
  });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in all fields.");
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center">
        <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-6" strokeWidth={1.6} />
        <h1 className="font-display text-4xl">Message received.</h1>
        <p className="mt-4 text-lg text-primary/80">I'll respond within 48 hours. If your question is claim-specific, consider starting the intake instead.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="label-caps mb-4">Contact</div>
        <h1 className="font-display text-4xl lg:text-5xl">Have a question?</h1>
        <p className="mt-5 text-lg text-primary/80">
          For claim-specific help, start the intake, that's how I collect the details I need to actually help. For general questions, use this form.
        </p>
        <div className="mt-10 space-y-5">
          <div className="flex items-start gap-4">
            <Mail className="w-6 h-6 text-primary flex-shrink-0 mt-1" strokeWidth={1.7} />
            <div>
              <div className="font-semibold">Email</div>
              <div className="text-primary/80">intake@veteransdisabilityeducation.com</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MessageSquare className="w-6 h-6 text-primary flex-shrink-0 mt-1" strokeWidth={1.7} />
            <div>
              <div className="font-semibold">Response time</div>
              <div className="text-primary/80">Within 48 business hours</div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        <form onSubmit={submit} className="bg-secondary p-8 lg:p-10 border-t-4 border-accent space-y-5" data-testid="contact-form">
          <div>
            <Label htmlFor="contact-name">Name</Label>
            <Input id="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white h-12" data-testid="contact-name" />
          </div>
          <div>
            <Label htmlFor="contact-email">Email</Label>
            <Input id="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-white h-12" data-testid="contact-email" />
          </div>
          <div>
            <Label htmlFor="contact-message">Message</Label>
            <Textarea id="contact-message" required rows={7} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 bg-white" data-testid="contact-message" placeholder="Ask a general question. For claim-specific help, use the intake form." />
          </div>
          <Button type="submit" size="lg" disabled={sending} className="w-full" data-testid="contact-submit">
            {sending ? "Sending…" : "Send message"} <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
